import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { emptyForm } from '../utils/customerHelpers';

type FormState = typeof emptyForm;

interface CustomerCreateModalProps {
  showForm: boolean;
  form: FormState;
  submitting: boolean;
  error: string | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}

const inputClass = 'w-full theme-border rounded px-2 py-1 text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent theme-bg-primary theme-text-primary';

const CustomerCreateModal: React.FC<CustomerCreateModalProps> = ({
  showForm,
  form,
  submitting,
  error,
  onChange,
  onSubmit,
  onClose,
}) => {
  if (!showForm) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] pt-20 pb-8"
      onClick={onClose}
    >
      <div
        className="theme-bg-primary rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[calc(100vh-8rem)] overflow-y-auto theme-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 theme-bg-primary border-b theme-border px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-lg font-semibold theme-text-primary">Create Client</h2>
          <button onClick={onClose} className="p-1 hover:opacity-80 rounded-full transition-opacity">
            <X className="w-4 h-4 theme-text-secondary" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-4 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">
              Item (Official Name) <span className="text-red-500">*</span>
            </label>
            <input name="item" value={form.item} onChange={onChange} required className={inputClass} placeholder="Enter official name" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">
                Client ID <span className="text-red-500">*</span>
              </label>
              <input name="client_id" value={form.client_id} onChange={onChange} required className={inputClass} placeholder="Enter client ID" />
            </div>
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">
                Short Name <span className="text-red-500">*</span>
              </label>
              <input name="short_name" value={form.short_name} onChange={onChange} required className={inputClass} placeholder="Enter short name" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">Monday ID</label>
            <input name="monday_id" value={form.monday_id} onChange={onChange} className={inputClass} placeholder="Enter Monday ID" />
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">Funding Type</label>
            <select name="funding_type" value={form.funding_type} onChange={onChange} className={inputClass}>
              <option value="">Select Funding Type</option>
              <option value="On-Call">On-Call</option>
              <option value="Project Management">Project Management</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">Status</label>
              <select name="status" value={form.status} onChange={onChange} className={inputClass}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium theme-text-primary mb-0.5">Client Drive Name</label>
              <input name="client_drive_name" value={form.client_drive_name} onChange={onChange} className={inputClass} placeholder="Enter drive name" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium theme-text-primary mb-0.5">Google Drive URL</label>
            <input name="google_drive_url" value={form.google_drive_url} onChange={onChange} type="url" className={inputClass} placeholder="https://drive.google.com/..." />
          </div>

          <div className="flex justify-end gap-2 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 theme-border rounded hover:opacity-80 transition-opacity text-sm theme-bg-secondary theme-text-primary"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded hover:shadow-lg transition-shadow disabled:opacity-50 text-sm"
            >
              {submitting ? 'Saving...' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default CustomerCreateModal;
